/**
 * EntryModelsSection
 *
 * Renders the Entry Models section inside the TradeDrawer.
 * Multi-select checkboxes — trader may select one or more entry model types.
 *
 * @see EntryModelSnapshot — entry model data shape
 */

// 1. External dependencies
import React from 'react';

// 2. Internal absolute imports
import { Checkbox } from '@enfomentis/ui';

// 3. Type imports
import type { EntryModelSnapshot } from '@/types/shared/StrategySnapshots';

// ===== TYPES =====

interface EntryModelsSectionProps {
  entryModels: EntryModelSnapshot[];
  onChange: (updated: EntryModelSnapshot[]) => void;
}

// ===== COMPONENT =====

export const EntryModelsSection: React.FC<EntryModelsSectionProps> = ({ entryModels, onChange }) => {
  const toggle = (entryModelId: string): void => {
    onChange(entryModels.map((em) => em.entryModelId === entryModelId ? { ...em, checked: !em.checked } : em));
  };

  if (entryModels.length === 0) {
    return (
      <p className="text-xs text-text-tertiary italic">Select a playbook to populate entry models.</p>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {entryModels.map((em) => (
        <Checkbox
          key={em.entryModelId}
          isSelected={em.checked}
          onValueChange={() => toggle(em.entryModelId)}
          size="sm"
          classNames={{ label: 'text-xs text-text-primary' }}
        >
          <div>
            <span>{em.name}</span>
            {em.description !== undefined && (
              <span className="block text-text-tertiary">{em.description}</span>
            )}
          </div>
        </Checkbox>
      ))}
    </div>
  );
};
